import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AlarmOnOutlinedIcon from '@mui/icons-material/AlarmOnOutlined';
import Typography from '@mui/material/Typography';
import IconLargeCicle from '../../shared/IconLargeCicle';
import CircularProgress from '@mui/material/CircularProgress';
import {
  useCancelScheduleByIdWithTokenMutation,
  useVerifyActiveScheduleByIdMutation,
  VerifyActiveScheduleDTO
} from '../../../services/schedule.service';
import { formatDateLabel } from '../../../lib/dayjs';
import ExpiredConfirmationSchedule from '../ExpiredConfirmationSchedule';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { useIsLoggedIn } from '../../../shared/hooks';
import { setRedirect } from '../../../features/redirect';
import { useDispatch } from 'react-redux';
import { dynamicCustomEventTracking } from '../../../lib/gtm';

/**
 * @deprecated Esta página é uma versão antiga da confirmação de agendamento
 */
export default function ConfirmationSignalScheduleDeprecatedRoot() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isLogged = useIsLoggedIn();
  const [isLoading, setIdLoading] = useState(true);
  const [isExpire, seIsExpire] = useState(true);
  const { scheduleId } = useParams();
  const [schedule, setSchedule] = useState<VerifyActiveScheduleDTO>();
  const [verifyActiveScheduleById] = useVerifyActiveScheduleByIdMutation();
  const [cancelScheduleByIdWithToken, { isLoading: isLoadingCancelJourney }] = useCancelScheduleByIdWithTokenMutation();
  const goToConfirmation = () => {
    navigate(`/confirmar-agendamento/${scheduleId}`, { state: { token: schedule?.token } });
  };

  const handleReschedule = () => {
    if (isLogged) {
      dynamicCustomEventTracking.send({
        dynamicEventName: 'signal_reschedule_logged_mc'
      });
      navigate(`/jornada/${schedule?.journey.id}`);
    } else {
      dynamicCustomEventTracking.send({
        dynamicEventName: 'signal_reschedule_logged_mc'
      });
      dispatch(setRedirect(`/jornada/${schedule?.journey.id}`));
      navigate(`/login`);
    }
  };

  const handleCancelSchedule = () => {
    cancelScheduleByIdWithToken({ id: schedule?.id || '', token: schedule?.token || '' })
      .unwrap()
      .then(res => {
        if (!res) {
          seIsExpire(true);
          return;
        }
        dynamicCustomEventTracking.send({
          dynamicEventName: 'signal_cancel_schedule_mc'
        });
        navigate(`/motivo-do-cancelamento/${schedule?.id}`, { state: { token: schedule?.token } });
      })
      .catch(() => {
        seIsExpire(true);
      });
  };

  useEffect(() => {
    verifyActiveScheduleById({ id: scheduleId || '' })
      .unwrap()
      .then(res => {
        if (!res) {
          seIsExpire(true);
          setIdLoading(false);
          return;
        }
        setIdLoading(false);
        seIsExpire(false);
        setSchedule(res);
      })
      .catch(() => {
        seIsExpire(true);
        setIdLoading(false);
      });
  }, []);

  if (isLoading || isLoadingCancelJourney) {
    return <CircularProgress sx={{ color: 'brand.darkGreen' }} />;
  }

  if (isExpire) {
    return <ExpiredConfirmationSchedule />;
  }

  return (
    <>
      <IconLargeCicle>
        <AlarmOnOutlinedIcon />
      </IconLargeCicle>

      <Typography variant="h2" sx={{ mb: 3 }}>
        Deseja confirmar seu atendimento?
      </Typography>
      <Typography variant="body1" sx={{ mb: 2 }}>
        Seu agendamento com a Dra. {schedule?.user?.name} está marcado para o dia{' '}
        {formatDateLabel(schedule?.dateStart || '')}!
      </Typography>
      <Typography variant="body1" sx={{ mb: 5 }}>
        Para que possamos nos preparar da melhor forma e garantir que tudo esteja perfeito para o seu atendimento, precisamos
        que você escolha uma das opções abaixo. Estamos aguardando sua resposta.
      </Typography>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap'
        }}
      >
        <Button variant="contained" color="secondary" onClick={() => handleReschedule()}>
          Reagendar
        </Button>

        <Button variant="contained" color="primary" onClick={() => goToConfirmation()}>
          Confirmar horário
        </Button>
      </Box>
      <Box sx={{ mt: 3 }}>
        <Button variant="inline" color="primary" onClick={() => handleCancelSchedule()}>
          Cancelar horário
        </Button>
      </Box>
    </>
  );
}
